import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  InputAdornment,
  SelectChangeEvent,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Event as EventIcon,
  Add as AddIcon,
  Info as InfoIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Timestamp, collection, addDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { mockEventbriteService } from '../../../services/mockEventbriteService';
import eventbriteService, { NormalizedEvent } from '../../../services/eventbriteService';
import { createApiUrl } from '../../../utils/apiConfig';

interface EventSearchProps {
   onEventSaved?: () => void;
}

const EventSearch: React.FC<EventSearchProps> = ({ onEventSaved }) => {
   // Search state
   const [searchTerm, setSearchTerm] = useState('');
   const [city, setCity] = useState('');
   const [startDate, setStartDate] = useState<Date | null>(null);
   const [endDate, setEndDate] = useState<Date | null>(null);
   const [eventType, setEventType] = useState('');

   // Results state
   const [events, setEvents] = useState<NormalizedEvent[]>([]);
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [selectedEvent, setSelectedEvent] = useState<NormalizedEvent | null>(null);

   // UI state
   const [detailsOpen, setDetailsOpen] = useState(false);
   const [saveSuccess, setSaveSuccess] = useState(false);
   const [savingEvent, setSavingEvent] = useState(false);

   // Flag to control API usage - default to using mock data
   const [useRealApi, setUseRealApi] = useState<boolean>(false);

   // Initialize API configuration
   useEffect(() => {
      // TODO: Initialize API token from Redux store
      console.warn('API token management moved to Redux. Using mock data for now.');
      setError('API token configuration moved to Redux. Please use mock data for testing.');
   }, []);

   // Handle search form submission
   const handleSearch = async () => {
      if (!searchTerm && !city && !startDate && !endDate && !eventType) {
         setError('Please enter at least one search criteria');
         return;
      }

      try {
         setLoading(true);
         setError(null);

         const searchParams = {
            q: searchTerm,
            city: city,
            startDate: startDate,
            endDate: endDate,
            onlineEvents:
               eventType === 'online' ? true : eventType === 'inperson' ? false : undefined,
            page: 1,
            pageSize: 25,
         };

         // Determine which service to use
         const service = useRealApi ? eventbriteService : mockEventbriteService;

         // When using real API, ensure the token is set
         if (useRealApi) {
            // TODO: Get API token from Redux store
            const apiToken: string = ''; // Disabled until Redux integration
            if (!apiToken) {
               throw new Error('API token is required to use the real Eventbrite API');
            }
            // This code will never execute because apiToken is empty and we throw above
            // eventbriteService.setApiToken(apiToken);
            // console.log('API token set:', apiToken.substring(0, 5) + '...');
         } else {
            console.log('Using mock Eventbrite service');
         }

         // Make the API call using the selected service
         const response = await service.searchEvents(searchParams);

         setEvents(response.events || []);

         if (response.events.length === 0) {
            setError('No events found matching your search criteria.');
         }
      } catch (err: any) {
         console.error('Error searching for events:', err);
         setError(err.message || 'An error occurred while searching for events.');
         setEvents([]);
      } finally {
         setLoading(false);
      }
   };

   // Reset search form
   const handleReset = () => {
      setSearchTerm('');
      setCity('');
      setStartDate(null);
      setEndDate(null);
      setEventType('');
      setEvents([]);
      setError(null);
   };

   // View event details
   const handleViewDetails = (event: NormalizedEvent) => {
      setSelectedEvent(event);
      setDetailsOpen(true);
   };

   // Close details dialog
   const handleCloseDetails = () => {
      setDetailsOpen(false);
   };

   // Save event to database
   const handleSaveEvent = async (event: NormalizedEvent) => {
      try {
         setSavingEvent(true);

         // Convert to firestore data structure
         const eventData = {
            title: event.title,
            description: event.description,
            startDate: Timestamp.fromDate(new Date(event.startDate)),
            endDate: Timestamp.fromDate(new Date(event.endDate)),
            location: {
               name: event.venue || '',
               address: event.location.address || '',
               city: event.location.city || '',
               state: event.location.state || '',
               postalCode: event.location.postalCode || '',
               country: event.location.country || '',
               latitude: event.location.latitude ? parseFloat(event.location.latitude) : null,
               longitude: event.location.longitude ? parseFloat(event.location.longitude) : null,
            },
            organizer: {
               name: event.organizerName || 'Unknown Organizer',
            },
            category: event.categories?.[0] || '',
            tags: event.tags || [],
            sourceId: event.sourceId,
            sourceName: 'eventbrite',
            sourceEventId: event.id,
            sourceUrl: event.url,
            isOnline: event.isOnline,
            status: 'active',
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
            lastSyncedAt: Timestamp.now(),
            imageUrl: event.imageUrl,
         };

         // Save using backend API to ensure proper members array
         const { createApiUrl } = await import('../../../utils/apiConfig');
         const apiUrl = createApiUrl('/api/events');

         const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
            },
            body: JSON.stringify({
               title: eventData.title,
               description: eventData.description,
               startDate: eventData.startDate.toDate().toISOString(),
               endDate: eventData.endDate.toDate().toISOString(),
               location: eventData.location,
               isPublic: false, // Default for imported events
               userId: 'admin-import', // Default user for admin imports
               // Include external source fields
               sourceEventId: eventData.sourceEventId,
               sourceUrl: eventData.sourceUrl,
               source: eventData.sourceName,
               imageUrl: eventData.imageUrl,
            }),
         });

         const result = await response.json();

         if (!response.ok) {
            throw new Error(result.error || 'Failed to import event');
         }

         setSaveSuccess(true);
         if (onEventSaved) {
            onEventSaved();
         }

         // Remove from the list to avoid duplicate saves
         setEvents(events.filter((e) => e.id !== event.id));
      } catch (err) {
         console.error('Error saving event:', err);
         setError('Failed to save event. Please try again.');
      } finally {
         setSavingEvent(false);
      }
   };

   // Close success notification
   const handleCloseSnackbar = () => {
      setSaveSuccess(false);
   };

   return (
      <Box sx={{ p: 2 }}>
         <Typography variant='h6' gutterBottom>
            Search Eventbrite Events
         </Typography>

         {true && ( // TODO: Check Redux store for token
            <Alert
               severity='info'
               sx={{ mb: 3 }}
               action={
                  <Button color='inherit' size='small'>
                     Set Token
                  </Button>
               }
            >
               <Typography variant='subtitle2'>Eventbrite API Setup Required</Typography>
               <Typography variant='body2'>
                  To use this feature, you need an Eventbrite API token. Follow these steps:
                  <ol>
                     <li>
                        Create an Eventbrite developer account at{' '}
                        <a
                           href='https://www.eventbrite.com/platform/'
                           target='_blank'
                           rel='noopener noreferrer'
                        >
                           eventbrite.com/platform
                        </a>
                     </li>
                     <li>Create a new application and generate a private token</li>
                     <li>Copy the token and paste it below</li>
                  </ol>
               </Typography>
               <TextField
                  label='Eventbrite Private Token'
                  variant='outlined'
                  size='small'
                  fullWidth
                  sx={{ mt: 1 }}
                  onChange={(e) => {
                     // TODO: Update token in Redux store instead
                     console.log('Token input changed. Redux integration needed.');
                     // Save the token for future use
                     localStorage.setItem('eventbriteToken', e.target.value);
                     // Update the API service
                     eventbriteService.setApiToken(e.target.value);
                  }}
               />
            </Alert>
         )}

         <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Grid container spacing={2}>
               <Grid item xs={12} md={4}>
                  <TextField
                     label='Search for Events'
                     placeholder='Keywords, event name, etc.'
                     value={searchTerm}
                     onChange={(e) => setSearchTerm(e.target.value)}
                     fullWidth
                     InputProps={{
                        startAdornment: (
                           <InputAdornment position='start'>
                              <SearchIcon />
                           </InputAdornment>
                        ),
                     }}
                  />
               </Grid>

               <Grid item xs={12} md={2}>
                  <TextField
                     label='City'
                     placeholder='e.g. Chicago'
                     value={city}
                     onChange={(e) => setCity(e.target.value)}
                     fullWidth
                     InputProps={{
                        startAdornment: (
                           <InputAdornment position='start'>
                              <LocationIcon />
                           </InputAdornment>
                        ),
                     }}
                  />
               </Grid>

               <Grid item xs={12} md={2}>
                  <TextField
                     label='Start Date'
                     type='date'
                     fullWidth
                     value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                     onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value) : null;
                        setStartDate(date);
                     }}
                     InputLabelProps={{
                        shrink: true,
                     }}
                  />
               </Grid>

               <Grid item xs={12} md={2}>
                  <TextField
                     label='End Date'
                     type='date'
                     fullWidth
                     value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                     onChange={(e) => {
                        const date = e.target.value ? new Date(e.target.value) : null;
                        setEndDate(date);
                     }}
                     InputLabelProps={{
                        shrink: true,
                     }}
                  />
               </Grid>

               <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                     <InputLabel id='event-type-label'>Event Type</InputLabel>
                     <Select
                        labelId='event-type-label'
                        value={eventType}
                        label='Event Type'
                        onChange={(e: SelectChangeEvent) => setEventType(e.target.value)}
                     >
                        <MenuItem value=''>All Events</MenuItem>
                        <MenuItem value='online'>Online Events</MenuItem>
                        <MenuItem value='inperson'>In-Person Events</MenuItem>
                     </Select>
                  </FormControl>
               </Grid>
            </Grid>

            <Box
               sx={{
                  mt: 2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
               }}
            >
               {/* Add a toggle switch for mock data vs. real API */}
               <FormControlLabel
                  control={
                     <Switch
                        checked={useRealApi}
                        onChange={(e) => setUseRealApi(e.target.checked)}
                        disabled={true} // TODO: Enable when Redux token is available
                     />
                  }
                  label={
                     <Box>
                        <Typography variant='body2'>
                           {useRealApi ? 'Using Real API' : 'Using Mock Data'}
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                           {true // TODO: Check Redux store for token
                              ? 'API token required for real data'
                              : useRealApi
                                ? 'Note: Eventbrite search API has been deprecated'
                                : 'Using reliable mock data for development'}
                        </Typography>
                     </Box>
                  }
               />

               <Box>
                  <Button variant='outlined' sx={{ mr: 1 }} onClick={handleReset}>
                     Reset
                  </Button>

                  <Button
                     variant='contained'
                     startIcon={<SearchIcon />}
                     onClick={handleSearch}
                     disabled={loading}
                  >
                     Search Events
                     {loading && <CircularProgress size={20} color='inherit' sx={{ ml: 1 }} />}
                  </Button>
               </Box>
            </Box>
         </Paper>

         {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
               <CircularProgress />
            </Box>
         )}

         {error && (
            <Alert severity='warning' sx={{ mb: 3 }}>
               {error}
            </Alert>
         )}

         {!loading && events.length > 0 && (
            <>
               <Typography variant='h6' sx={{ mb: 2 }}>
                  Search Results ({events.length} events found)
               </Typography>

               <TableContainer component={Paper} sx={{ mb: 3 }}>
                  <Table>
                     <TableHead>
                        <TableRow>
                           <TableCell>Event</TableCell>
                           <TableCell>Date & Time</TableCell>
                           <TableCell>Location</TableCell>
                           <TableCell>Organizer</TableCell>
                           <TableCell>Actions</TableCell>
                        </TableRow>
                     </TableHead>
                     <TableBody>
                        {events.map((event) => (
                           <TableRow key={event.id}>
                              <TableCell>
                                 <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography variant='body1' fontWeight='medium'>
                                       {event.title}
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                       {event.isOnline && (
                                          <Chip
                                             size='small'
                                             label='Online'
                                             color='primary'
                                             sx={{ mr: 1 }}
                                          />
                                       )}
                                       {event.isFree && (
                                          <Chip
                                             size='small'
                                             label='Free'
                                             color='success'
                                             sx={{ mr: 1 }}
                                          />
                                       )}
                                       {event.categories?.map((category) => (
                                          <Chip
                                             key={category}
                                             size='small'
                                             label={category}
                                             sx={{ mr: 1 }}
                                          />
                                       ))}
                                    </Box>
                                 </Box>
                              </TableCell>
                              <TableCell>
                                 <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Box>
                                       <Typography variant='body2'>
                                          {format(new Date(event.startDate), 'MMM dd, yyyy')}
                                       </Typography>
                                       <Typography variant='body2' color='textSecondary'>
                                          {format(new Date(event.startDate), 'h:mm a')} -
                                          {format(new Date(event.endDate), 'h:mm a')}
                                       </Typography>
                                    </Box>
                                 </Box>
                              </TableCell>
                              <TableCell>
                                 {event.isOnline ? (
                                    <Typography variant='body2'>Online Event</Typography>
                                 ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                       <LocationIcon sx={{ mr: 1, mt: 0.5, color: 'error.main' }} />
                                       <Box>
                                          <Typography variant='body2'>
                                             {event.venue || event.location.name}
                                          </Typography>
                                          <Typography variant='body2' color='textSecondary'>
                                             {event.location.city}, {event.location.state}
                                          </Typography>
                                       </Box>
                                    </Box>
                                 )}
                              </TableCell>
                              <TableCell>
                                 <Typography variant='body2'>
                                    {event.organizerName || 'Unknown Organizer'}
                                 </Typography>
                              </TableCell>
                              <TableCell>
                                 <Box sx={{ display: 'flex' }}>
                                    <Tooltip title='View Details'>
                                       <IconButton
                                          onClick={() => handleViewDetails(event)}
                                          size='small'
                                       >
                                          <InfoIcon />
                                       </IconButton>
                                    </Tooltip>
                                    <Tooltip title='Save Event'>
                                       <IconButton
                                          onClick={() => handleSaveEvent(event)}
                                          size='small'
                                          color='primary'
                                          disabled={savingEvent}
                                       >
                                          <SaveIcon />
                                       </IconButton>
                                    </Tooltip>
                                 </Box>
                              </TableCell>
                           </TableRow>
                        ))}
                     </TableBody>
                  </Table>
               </TableContainer>
            </>
         )}

         {/* Event Details Dialog */}
         <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth='md' fullWidth>
            {selectedEvent && (
               <>
                  <DialogTitle>{selectedEvent.title}</DialogTitle>
                  <DialogContent dividers>
                     <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                           {selectedEvent.imageUrl && (
                              <Box sx={{ mb: 2, borderRadius: 1, overflow: 'hidden' }}>
                                 <img
                                    src={selectedEvent.imageUrl}
                                    alt={selectedEvent.title}
                                    style={{
                                       width: '100%',
                                       height: 'auto',
                                       maxHeight: '300px',
                                       objectFit: 'cover',
                                    }}
                                 />
                              </Box>
                           )}

                           <Typography variant='h6' gutterBottom>
                              Description
                           </Typography>
                           <Typography variant='body2' sx={{ whiteSpace: 'pre-line', mb: 3 }}>
                              {selectedEvent.description}
                           </Typography>
                        </Grid>

                        <Grid item xs={12} md={4}>
                           <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
                              <Typography variant='subtitle2' gutterBottom>
                                 Date and Time
                              </Typography>
                              <Box sx={{ display: 'flex', mb: 2 }}>
                                 <EventIcon sx={{ mr: 1, color: 'primary.main' }} />
                                 <Box>
                                    <Typography variant='body2'>
                                       {format(
                                          new Date(selectedEvent.startDate),
                                          'EEEE, MMMM dd, yyyy'
                                       )}
                                    </Typography>
                                    <Typography variant='body2'>
                                       {format(new Date(selectedEvent.startDate), 'h:mm a')} -
                                       {format(new Date(selectedEvent.endDate), 'h:mm a')}
                                    </Typography>
                                 </Box>
                              </Box>

                              <Typography variant='subtitle2' gutterBottom>
                                 Location
                              </Typography>
                              {selectedEvent.isOnline ? (
                                 <Typography variant='body2' sx={{ mb: 2 }}>
                                    Online Event
                                 </Typography>
                              ) : (
                                 <Box sx={{ display: 'flex', mb: 2 }}>
                                    <LocationIcon sx={{ mr: 1, mt: 0.5, color: 'error.main' }} />
                                    <Box>
                                       <Typography variant='body2'>
                                          {selectedEvent.venue || selectedEvent.location.name}
                                       </Typography>
                                       <Typography variant='body2'>
                                          {selectedEvent.location.address}
                                       </Typography>
                                       <Typography variant='body2'>
                                          {selectedEvent.location.city},{' '}
                                          {selectedEvent.location.state}{' '}
                                          {selectedEvent.location.postalCode}
                                       </Typography>
                                       <Typography variant='body2'>
                                          {selectedEvent.location.country}
                                       </Typography>
                                    </Box>
                                 </Box>
                              )}

                              <Typography variant='subtitle2' gutterBottom>
                                 Organizer
                              </Typography>
                              <Typography variant='body2' sx={{ mb: 2 }}>
                                 {selectedEvent.organizerName || 'Unknown Organizer'}
                              </Typography>

                              <Typography variant='subtitle2' gutterBottom>
                                 Categories & Tags
                              </Typography>
                              <Box sx={{ mb: 2 }}>
                                 {selectedEvent.categories?.map((category) => (
                                    <Chip
                                       key={category}
                                       size='small'
                                       label={category}
                                       sx={{ mr: 1, mb: 1 }}
                                    />
                                 ))}
                                 {selectedEvent.tags?.map((tag) => (
                                    <Chip
                                       key={tag}
                                       size='small'
                                       label={tag}
                                       variant='outlined'
                                       sx={{ mr: 1, mb: 1 }}
                                    />
                                 ))}
                              </Box>

                              <Typography variant='subtitle2' gutterBottom>
                                 Event Link
                              </Typography>
                              <Button
                                 href={selectedEvent.url}
                                 target='_blank'
                                 rel='noopener noreferrer'
                                 variant='outlined'
                                 size='small'
                                 fullWidth
                              >
                                 View on Eventbrite
                              </Button>
                           </Paper>
                        </Grid>
                     </Grid>
                  </DialogContent>
                  <DialogActions>
                     <Button onClick={handleCloseDetails}>Close</Button>
                     <Button
                        variant='contained'
                        startIcon={<SaveIcon />}
                        onClick={() => {
                           handleSaveEvent(selectedEvent);
                           handleCloseDetails();
                        }}
                        disabled={savingEvent}
                     >
                        Save Event
                     </Button>
                  </DialogActions>
               </>
            )}
         </Dialog>

         {/* Success notification */}
         <Snackbar
            open={saveSuccess}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            message={
               <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CheckCircleIcon sx={{ mr: 1 }} />
                  Event saved successfully!
               </Box>
            }
         />
      </Box>
   );
};

export default EventSearch;
